import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { BtlHelperService } from '@base/services/btl-helper.service';
import { UniversalStorageService } from '@base/services/universal-storage.service';

import { JsonProduct } from '@catalog/products/interface/context';
import { CountableResult, PartnerProduct } from '@catalog/products/interface/phone';
import { Sensation } from '@catalog/products/subscription/plan/premium/sensation';
import { AnimCo } from '@catalog/products/view/product-feature.interface';

import { DateUniversalBase } from '@app/baseBtl/base/class/date-universal.class';
import {
    CritereRepriseParProduit,
    DevisRepriseMobileIn,
    DevisRepriseMobileOut,
    RecoveryStatus,
    RecoveryStorage,
} from '@app/modules/mobile-recovery/modal-recovery/interface/modal-recovery-model';
import { Oauth2RessourceService } from '@app/oauth2/oauth2-resources.service';

import { ModalService } from '@trilogy';
import { Observable, ReplaySubject } from 'rxjs';

import { ModalRecoveryComponent } from './modal-recovery/modal-recovery.component';

@Injectable({
    providedIn: 'root',
})
export class MobileRecoveryService extends UniversalStorageService {
    public KEY: { [index: string]: any } = {
        RECOVERY_IS_RENEWAL: 'recoveryIsRenewal',
        RECOVERY_SELECTED_PHONE: 'recoverySelectedPhone',
    };

    protected sessionStorageKeys: string[] = Object.values(this.KEY);

    public readonly TAGS = {
        ESTIMATE: 'reprise_mobile',
        FINISH: 'finaliser_ma_reprise',
    };

    protected data;
    public recoveryStorage: RecoveryStorage = {} as RecoveryStorage;

    private readonly STORAGE_KEY: string = 'mobile_recovery_current_product';
    public readonly MOBILE_RECOVERY_STEP_STORAGE_KEY: string = 'mobileRecoveryCurrentStep';

    public phoneSubject: ReplaySubject<AnimCo> = new ReplaySubject<AnimCo>();
    public phoneObs: Observable<AnimCo> = this.phoneSubject.asObservable();
    public product: JsonProduct;
    public parentProduct: JsonProduct;
    public isRenewal: boolean;
    public isWithoutTax: boolean = false;
    private _plan: JsonProduct;

    constructor(
        private btlHelper: BtlHelperService,
        private oauth2Resource: Oauth2RessourceService,
        private router: Router,
        private modalService: ModalService,
    ) {
        super();
        this.sessionStorageKeys.push(this.STORAGE_KEY, this.MOBILE_RECOVERY_STEP_STORAGE_KEY);
        this.recoveryStorage = this.getItem(this.STORAGE_KEY);

        if (!this.recoveryStorage) {
            this.initDataStorage();
        }
    }

    public setPlan(plan: JsonProduct): void {
        this._plan = plan;
        this.phoneSubject.next(null);
        this.initDataStorage();
    }

    public setParentProduct(product: JsonProduct): void {
        this.parentProduct = product;
    }

    public cancelMobileRecovery(): void {
        this.recoveryStorage = {} as RecoveryStorage;
        this.removeItem(this.STORAGE_KEY);
        this.removeItem(this.MOBILE_RECOVERY_STEP_STORAGE_KEY);
        this.setRecoveryStatus(RecoveryStatus.UNSTARTED);
        this.initDataStorage();
        this.phoneSubject.next(null);
    }

    public initDataStorage(): void {
        this.clearStorage();
    }

    private clearStorage(forceInit: boolean = false): void {
        if (!this.product) {
            return;
        }

        const bonus = this.getBonus();
        const dates = {
            start: this.product.recovery_from_date,
            end: this.product.recovery_to_date,
        };
        const productStorage = {
            product: {
                bonus,
                dates,
                hasBonus: !!bonus,
                item: {
                    name: this.product.name,
                    url: this.product.url,
                },
            },
        };

        let recoveryData = {};
        // compatibilites ancien parcours
        if ((this.recoveryStorage && !this.recoveryStorage.recovery) || forceInit) {
            recoveryData = {
                recovery: {
                    criterias: null,
                    estimation: null,
                    product: null,
                },
            };
        }

        // compatibilites ancien parcours checkout
        if (this.recoveryStorage && this.recoveryStorage.step) {
            delete this.recoveryStorage.step;
        }

        this.recoveryStorage = { ...this.recoveryStorage, ...productStorage, ...recoveryData };
    }

    /**
     * clean si reprise null
     *
     */
    public clearRecoveryStorage(): void {
        if (this.getAmountSaved() < 10) {
            this.clearStorage(true);
            this.setRecoveryStatus(RecoveryStatus.UNSTARTED);
        }
    }

    public setProduct(product: JsonProduct) {
        this.product = product;
        this.initDataStorage();
    }

    public goToRecoveryPage(): void {
        let url = '/recovery';
        if (this.recoveryStorage.status === RecoveryStatus.UNFINISHED) {
            url += '/index/listing';
        }
        this.btlHelper.navigateToExternalURL(url);
    }

    /**
     *@param:productName:string, telephone name
     *@return:observable,product bonus info
     */
    public getProduitsPartenaire(productName: string): Observable<CountableResult<PartnerProduct>> {
        return this.oauth2Resource.useSalesApi().produitsPartenaires().setParams({ libelle: productName }).get(false);
    }

    public getProduitsPartenaireCriteres(idPhone: string): Observable<CritereRepriseParProduit> {
        return this.oauth2Resource.useSalesApi().produitsPartenairesCriteres(idPhone).get(false);
    }

    public getProduitsPartenaireDevis(idPhone: string, data: DevisRepriseMobileOut): Observable<DevisRepriseMobileIn> {
        return this.oauth2Resource.useSalesApi().produitsPartenairesDevis(idPhone).post(data);
    }

    public getBonus(): number {
        return this.hasBonus() ? Number(this.parentProduct?.bonus_recovery || this.recoveryStorage?.product?.bonus) : 0;
    }

    public hasBonus(): boolean {
        const hasPlanIsPremium =
            this._plan &&
            ((this._plan.type_id === Sensation.PLAN_TYPE && !this._plan.low_prices_wall) ||
                this._plan.type_id === 'plan_premium_renew');

        if (!hasPlanIsPremium || (!this.parentProduct && !this.recoveryStorage)) {
            return false;
        }

        const bonus = this.parentProduct?.bonus_recovery || this.recoveryStorage?.product?.bonus;
        const startDate = this.parentProduct?.recovery_from_date || this.recoveryStorage?.product?.dates?.start;
        const endDate = this.parentProduct?.recovery_to_date || this.recoveryStorage?.product?.dates?.end;

        const now = new DateUniversalBase('now');
        const start = new DateUniversalBase(startDate);
        const end = new DateUniversalBase(endDate);

        return DateUniversalBase.dateBetween(start, end, now) && Number(bonus) > 0 && hasPlanIsPremium;
    }

    public getRecoveryPhoneName(response: any): string {
        let responsePhoneName: string;
        if (response && response.items[0] && response.items[0].nom) {
            responsePhoneName = response.items[0].nom;
        }
        return responsePhoneName;
    }

    public getAmountSavedExample(response: any): number {
        return response.items[0] && response.items[0].montantMax ? response.items[0].montantMax : 0;
    }

    public getAmountSaved(): number {
        return this.recoveryStorage.recovery?.estimation?.amount || 0;
    }

    public getRecoveredPhoneName(): string {
        return this.recoveryStorage.recovery && this.recoveryStorage.recovery.product
            ? this.recoveryStorage.recovery.product.name
            : '';
    }

    public isRecoveryFinished(): boolean {
        return this.recoveryStorage.status === RecoveryStatus.COMPLETED;
    }

    public isRecoveryUnstarted(): boolean {
        return this.recoveryStorage.status === RecoveryStatus.UNSTARTED || !this.recoveryStorage.status;
    }

    public isRecoveryOnGoing(): boolean {
        const status: string = this.recoveryStorage && this.recoveryStorage.status ? this.recoveryStorage.status : null;
        return status ? status === RecoveryStatus.UNFINISHED : false;
    }

    public hasIbanStorage(): boolean {
        return !!this.getIbanStorage();
    }

    public getIbanStorage(): string {
        return this.recoveryStorage && this.recoveryStorage.iban ? this.recoveryStorage.iban : null;
    }

    public getPhoneName(product: JsonProduct): string {
        const name = product.title;
        const firstLetter = name.trim().slice(0, 1).toLowerCase();
        switch (firstLetter) {
            case 'a':
            case 'e':
            case 'i':
            case 'h':
            case 'o':
                return `cet ${name}`;
            default:
                return `ce ${name}`;
        }
    }

    public initStorageListing(critereRepriseParProduit: CritereRepriseParProduit): void {
        this.recoveryStorage.recovery = {
            product: { brand: critereRepriseParProduit.marque, name: critereRepriseParProduit.nom },
            criterias: { id: critereRepriseParProduit.id, items: critereRepriseParProduit.criteresRepriseProduit },
        };
        this.updateRecoveryInStorage({ recovery: this.recoveryStorage.recovery });
    }

    public initStorageResult(
        devisRepriseMobileOut: DevisRepriseMobileOut,
        devisRepriseMobileIn: DevisRepriseMobileIn,
        idPhone: string,
    ): void {
        const estimationObject = {
            estimation: {
                amount: devisRepriseMobileIn.montantEstimeReprise,
                id: devisRepriseMobileIn.id,
                items: {
                    criteresReprise: devisRepriseMobileOut.criteresReprise,
                    id: idPhone,
                },
            },
        };
        this.recoveryStorage.recovery_id = devisRepriseMobileIn.id;
        this.recoveryStorage.recovery = { ...this.recoveryStorage.recovery, ...estimationObject };
        this.updateRecoveryInStorage({ recovery_id: devisRepriseMobileIn.id, recovery: this.recoveryStorage.recovery });
    }

    public setRecoveryStatus(status: RecoveryStatus): void {
        this.recoveryStorage.status = status;
        this.updateRecoveryInStorage({ status });
    }

    public setIban(iban?: string): void {
        delete this.recoveryStorage.iban;
        if (iban) {
            this.recoveryStorage.iban = iban.replace(/\s/g, '').trim();
        }
        this.updateRecoveryInStorage({ iban });
    }

    public saveRecoveryInfos(): void {
        this.updateRecoveryInStorage({ recovery: this.recoveryStorage.recovery });
    }

    private updateRecoveryInStorage(recoveryInfosToSave: Partial<RecoveryStorage>): void {
        const recoveryInfoFromStorage = this.getItem(this.STORAGE_KEY);
        const infosToSave: RecoveryStorage = { ...recoveryInfoFromStorage, ...recoveryInfosToSave };

        this.setItem(this.STORAGE_KEY, infosToSave, null, true);
    }

    public setDevis(id: string, devis: DevisRepriseMobileOut): void {
        sessionStorage.setItem(`recoveryDevisRepriseMobileOut-${id}`, JSON.stringify(devis));
    }

    public getDevis(id: string): DevisRepriseMobileOut {
        const devisOut = sessionStorage.getItem(`recoveryDevisRepriseMobileOut-${id}`);
        return devisOut ? JSON.parse(devisOut) : null;
    }

    public isRecoveryRenewal(): boolean {
        return this.router.url?.includes('renouvellement');
    }

    public saveBonus(phone?: JsonProduct, plan?: JsonProduct): void {
        if (plan) {
            this.setPlan(plan);
        }

        if (this.recoveryStorage?.product) {
            this.recoveryStorage.product.bonus = this.getBonus();
            this.recoveryStorage.product.hasBonus = this.hasBonus();
            this.updateRecoveryInStorage({ product: this.recoveryStorage.product });
        }
    }

    public openModal() {
        const modal = this.modalService.open(ModalRecoveryComponent, { modalSize: 'large' });
        modal.afterClosed.subscribe(() => {
            this.phoneSubject.next(null);
        });
    }

    public closeModal() {
        if (this.modalService?.modalComponentRef?.instance) {
            this.modalService.modalComponentRef.instance.closeClicked();
        }
    }
}
